import React, { useState } from 'react';
import { Divider, Stack } from '@mui/material';

import { CeilingPlane } from '@paragon/contract-library';
import { EditedCeilingPlaneProperties } from '@paragon/layout';
import { PropertiesPanel } from '@paragon/layout-models';

import { TransformationButtons } from '../../../../../library/transformations/controls/TransformationButtons';
import {
  beginCuttingCeilingPlane,
  editCeilingPlanes,
  finishCuttingCeilingPlane,
} from '../../../../../store/actions/layout/ceilingPlaneActions';
import { isLayoutModeViewOnly } from '../../../../../store/selectors/layoutModeSelectors';
import { propertiesShouldBeViewOnly } from '../../../../../store/selectors/viewOnlyModeSelectors';
import { useAppDispatch, useAppSelector } from '../../../../hooks';
import { ProjectHierarchyContextSelects } from '../ProjectHierarchyContextSelects';
import { PropertiesPanelMenuButton } from '../PropertiesPanelMenuButton';
import { PlaneCutsEditor } from './PlaneCutsEditor';
import { PlanePropertyControls } from './PlanePropertyControls';

/*
  Note that we strongly desire to keep this panel as visually similar to the roof plane properties panel as possible, as
    well as the bulk properties panels for roof and ceiling planes. Please consider updating all these panels when
    making any changes here.
 */

export function CeilingPlaneProperties(props: { value: CeilingPlane }) {
  const dispatch = useAppDispatch();
  const viewOnlyProperties = useAppSelector(propertiesShouldBeViewOnly);
  const viewOnlyLayout = useAppSelector(isLayoutModeViewOnly);
  const viewOnly = viewOnlyProperties || viewOnlyLayout;
  const [isSaving, setIsSaving] = useState(false);

  async function onChange(editedProperties: EditedCeilingPlaneProperties) {
    if (isSaving) {
      throw new Error('Overlapping save of roof plane prevented. Someone forgot to disable a control when saving.');
    }

    setIsSaving(true);

    try {
      await dispatch(editCeilingPlanes({ guid: props.value.guid, editedProperties }));
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <PropertiesPanel
      title="Ceiling Plane Properties"
      titleButton={<PropertiesPanelMenuButton type="ceilingPlane" guid={props.value.guid} />}
    >
      <div className="clickable-during-picking">
        <Stack spacing={2}>
          <TransformationButtons />

          <Divider />

          <ProjectHierarchyContextSelects
            levelGuid={props.value.levelGuid}
            onChange={async (levelGuid) => await onChange({ levelGuid })}
            disabled={viewOnly}
          />

          <PlanePropertyControls
            type="ceiling"
            properties={props.value}
            onChange={async (changes) => {
              await onChange(changes);
            }}
            disabled={viewOnly}
          />

          <Divider />

          <PlaneCutsEditor
            basePlaneGuid={props.value.guid}
            cuts={props.value.cuts}
            onStartPicking={async () => {
              await dispatch(beginCuttingCeilingPlane(props.value.guid));
            }}
            onFinishPicking={() => {
              dispatch(finishCuttingCeilingPlane());
            }}
            disabled={viewOnly}
          />
        </Stack>
      </div>
    </PropertiesPanel>
  );
}
